import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { Button } from './ui/Button'
import { Badge, StatusBadge } from './ui/Badge'
import { UploadVendorResponsesModal } from './modals/UploadVendorResponsesModal'
import { vendorsApi, evaluationsApi, projectsApi, apiClient } from '../lib/api'
import toast from 'react-hot-toast'
import { 
  FileText, 
  Calendar,
  Users,
  Search,
  Filter,
  Eye,
  MoreVertical,
  FolderOpen,
  Trash2,
  RefreshCw,
  RotateCcw,
  ChevronDown,
  Download
} from 'lucide-react'
import { sendEvent } from '@shared/telemetry'
import { formatDate, formatRelativeTime } from '../lib/utils'
import { generateVendorResponsesExcel } from '../lib/exportUtils'

interface VendorResponse {
  id: string
  vendor_name: string
  vendor_email?: string
  submission_date: string
  file_count: number
  file_names: string[]
  status: 'pending' | 'evaluated' | 'ready_for_review' | 'approved' | 'rejected'
  status_display: string
  page_count?: number
  total_file_size?: number
  unified_document_path?: string
  overall_requirement_status: 'meets' | 'partially_meets' | 'not_met' | 'pending'
  submitted_count?: number
  not_submitted_count?: number
  mandatory_not_submitted_count?: number
}

interface Project {
  id: string
  title: string
  status: string
  due_date?: string
  vendor_responses?: VendorResponse[]
  extraction_status?: 'pending' | 'in_progress' | 'completed' | 'failed'
  extraction_error?: string
  requirements?: any[]
}

interface PaginationProps {
  currentPage: number
  pageSize: number
  totalCount: number
  totalPages: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
}

interface ResponsesTabProps {
  project: Project
  pagination?: PaginationProps
  onAddResponse?: () => void
  onUploadVendorResponses?: (vendors: Array<{vendorName: string, files: File[]}>) => void
  onRefresh?: () => void
}

export const ResponsesTab: React.FC<ResponsesTabProps> = ({
  project,
  pagination,
  onAddResponse,
  onUploadVendorResponses,
  onRefresh
}) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [evaluatingIds, setEvaluatingIds] = useState<Set<string>>(new Set())
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)


  const filteredResponses = project.vendor_responses?.filter(response => {
    const matchesSearch = !searchQuery || 
      response.vendor_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      response.vendor_email?.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesStatus = !statusFilter || response.status === statusFilter
    
    return matchesSearch && matchesStatus
  }) || []

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-gray-100 text-gray-600'
      case 'evaluated': return 'bg-blue-100 text-blue-800'
      case 'ready_for_review': return 'bg-yellow-100 text-yellow-800'
      case 'approved': return 'bg-green-100 text-green-800'
      case 'rejected': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getRequirementStatusColor = (status: string) => {
    switch (status) {
      case 'meets': return 'bg-green-100 text-green-800'
      case 'partially_meets': return 'bg-yellow-100 text-yellow-800'
      case 'not_met': return 'bg-red-100 text-red-800'
      case 'pending': return 'bg-gray-100 text-gray-600'
      default: return 'bg-gray-100 text-gray-600'
    }
  }

  const getRequirementStatusLabel = (status: string) => {
    switch (status) {
      case 'meets': return 'Yes'
      case 'partially_meets': return 'Partially'
      case 'not_met': return 'No'
      case 'pending': return 'Pending'
      default: return 'N/A'
    }
  }

  const getStatusDisplayLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'Pending'
      case 'evaluated': return 'Evaluated'
      case 'ready_for_review': return 'Ready for Review'
      case 'approved': return 'Approved'
      case 'rejected': return 'Rejected'
      default: return status.charAt(0).toUpperCase() + status.slice(1)
    }
  }


  const handleDeleteResponse = async (responseId: string) => {
    if (!window.confirm('Are you sure you want to delete this vendor response? This action cannot be undone.')) {
      return
    }

    setDeletingId(responseId)
    
    try {
      await vendorsApi.delete(responseId)
      toast.success('Vendor response deleted successfully')
      if (onRefresh) {
        onRefresh()
      }
    } catch (error) {
      toast.error('Failed to delete vendor response')
      console.error('Error deleting vendor response:', error)
    } finally {
      setDeletingId(null)
    }
  }

  const handleDownloadUnifiedDocument = async (response: VendorResponse) => {
    if (!response.unified_document_path) {
      toast.error('Unified document not available')
      return
    }
    try {
      const encodedPath = encodeURIComponent(response.unified_document_path)
      await apiClient.download(`/files/stream/${encodedPath}`, `${response.vendor_name}_unified.pdf`)
      sendEvent('unified_document_downloaded', {
        project_id: project.id,
        vendor_response_id: response.id,
        vendor_name: response.vendor_name,
      })
      toast.success('Download started')
    } catch (error) {
      toast.error('Failed to download unified document')
      console.error('Error downloading unified document:', error)
    }
  }

  const handleReEvaluateResponse = async (responseId: string, force: boolean = true) => {
    if (!window.confirm('Are you sure you want to re-evaluate this vendor response? This will overwrite existing evaluations.')) {
      return
    }

    setEvaluatingIds(prev => new Set([...prev, responseId]))

    try {
      const result = await evaluationsApi.evaluateResponse(responseId, { force_reevaluation: force })
      sendEvent('vendor_response_reevaluated', {
        project_id: project.id,
        source: 'single',
        response_count: 1,
        task_id: result.task_id,
      })
      toast.success('Vendor response re-evaluation started')

      // Immediate refresh - triggers auto-polling to show "Pending" status
      if (onRefresh) {
        onRefresh()
      }

      // Poll for completion (like new vendor evaluation does)
      const taskId = result.task_id
      const pollInterval = setInterval(async () => {
        try {
          const status = await api.get(`/tasks/${taskId}/status`)

          if (status.data.status === 'completed') {
            clearInterval(pollInterval)
            toast.success('Re-evaluation completed!')

            // Invalidate cache and refresh
            queryClient.invalidateQueries(['evaluations', responseId])
            if (onRefresh) {
              onRefresh()
            }
            setEvaluatingIds(prev => {
              const newSet = new Set(prev)
              newSet.delete(responseId)
              return newSet
            })
          } else if (status.data.status === 'failed') {
            clearInterval(pollInterval)
            toast.error('Re-evaluation failed')
            setEvaluatingIds(prev => {
              const newSet = new Set(prev)
              newSet.delete(responseId)
              return newSet
            })
          }
        } catch (error) {
          clearInterval(pollInterval)
          console.error('Error polling task status:', error)
          setEvaluatingIds(prev => {
            const newSet = new Set(prev)
            newSet.delete(responseId)
            return newSet
          })
        }
      }, 2000) // Poll every 2 seconds

    } catch (error) {
      toast.error('Failed to start re-evaluation')
      console.error('Error re-evaluating vendor response:', error)
      setEvaluatingIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(responseId)
        return newSet
      })
    }
  }

  const handleReEvaluateAllResponses = async () => {
    const responseIds = filteredResponses.map(r => r.id)
    if (responseIds.length === 0) {
      toast.error('No vendor responses to re-evaluate')
      return
    }

    if (!window.confirm(`Are you sure you want to re-evaluate all ${responseIds.length} vendor responses? This will overwrite existing evaluations.`)) {
      return
    }

    // Set all responses as evaluating
    setEvaluatingIds(new Set(responseIds))

    try {
      const result = await evaluationsApi.batchEvaluate({ vendor_response_ids: responseIds, force_reevaluation: true })

      sendEvent('vendor_response_reevaluated', {
        project_id: project.id,
        source: 'bulk',
        response_count: responseIds.length,
        task_id: result.task_id,
      })

      // Returns immediately with task_id!
      toast.success(`Started re-evaluation of ${responseIds.length} vendor responses`)

      // Immediate refresh - triggers auto-polling to show "Pending" status
      if (onRefresh) {
        onRefresh()
      }

      // Poll for completion (like new vendor evaluation does)
      const taskId = result.task_id
      const pollInterval = setInterval(async () => {
        try {
          const status = await api.get(`/tasks/${taskId}/status`)

          if (status.data.status === 'completed') {
            clearInterval(pollInterval)
            toast.success('Re-evaluation completed!')

            // Invalidate cache and refresh
            responseIds.forEach(id => {
              queryClient.invalidateQueries(['evaluations', id])
            })
            if (onRefresh) {
              onRefresh()
            }
            setEvaluatingIds(new Set())
          } else if (status.data.status === 'failed') {
            clearInterval(pollInterval)
            toast.error('Re-evaluation failed')
            setEvaluatingIds(new Set())
          }
        } catch (error) {
          clearInterval(pollInterval)
          console.error('Error polling task status:', error)
          setEvaluatingIds(new Set())
        }
      }, 2000) // Poll every 2 seconds

    } catch (error) {
      toast.error('Failed to start batch re-evaluation')
      console.error('Error re-evaluating vendor responses:', error)
      setEvaluatingIds(new Set())
    }
  }

  const handleStatusChange = async (responseId: string, newStatus: string) => {
    setUpdatingStatusId(responseId)
    
    try {
      await vendorsApi.update(responseId, { status: newStatus })
      toast.success('Status updated successfully')
      if (onRefresh) {
        onRefresh()
      }
    } catch (error) {
      toast.error('Failed to update status')
      console.error('Error updating status:', error)
    } finally {
      setUpdatingStatusId(null)
    }
  }

  const handleExportToExcel = async () => {
    if (!project || !project.vendor_responses || project.vendor_responses.length === 0) {
      toast.error('No vendor responses to export')
      return
    }

    setIsExporting(true)
    
    try {
      // Try to fetch detailed evaluations for better export data
      let evaluations: any[] = []
      let requirements: any[] = []
      
      try {
        // Fetch requirements for the project
        requirements = await projectsApi.getRequirements(project.id)
      } catch (error) {
        console.warn('Could not fetch project requirements:', error)
      }
      
      try {
        // Fetch evaluations for the project if available
        const evaluationPromises = project.vendor_responses.map(async (response) => {
          try {
            return await evaluationsApi.getResponseEvaluations(response.id)
          } catch (error) {
            // If evaluations aren't available for this response, continue without them
            console.warn(`No evaluations found for response ${response.id}`)
            return []
          }
        })
        
        const evaluationResults = await Promise.all(evaluationPromises)
        evaluations = evaluationResults.flatMap(result => result || [])
      } catch (error) {
        // If we can't fetch evaluations, proceed with basic export
        console.warn('Could not fetch detailed evaluations, proceeding with basic export:', error)
      }

      // Generate and download Excel file
      await generateVendorResponsesExcel(project, evaluations, requirements)
      toast.success('Excel file downloaded successfully')
      
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Failed to export data. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  // Check if requirements are extracted
  const requirementsNotReady = !project.extraction_status || 
    project.extraction_status === 'pending' || 
    project.extraction_status === 'in_progress' ||
    project.extraction_status === 'failed'

  const canUploadResponses = project.extraction_status === 'completed' && 
    project.requirements && 
    project.requirements.length > 0

  return (
    <div className="space-y-6">
      {/* Show message if requirements not extracted */}
      {requirementsNotReady && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                {project.extraction_status === 'in_progress' 
                  ? 'Requirements are being extracted'
                  : project.extraction_status === 'failed'
                  ? 'Requirements extraction failed'
                  : 'Requirements not yet extracted'
                }
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  {project.extraction_status === 'in_progress' 
                    ? 'Please wait while we extract requirements from the RFP document. You will be able to upload vendor responses once this process is complete.'
                    : project.extraction_status === 'failed'
                    ? `Requirements extraction failed: ${project.extraction_error || 'Unknown error'}. Please contact support.`
                    : 'You need to extract requirements from the RFP document before you can upload and evaluate vendor responses.'
                  }
                </p>
              </div>
              {project.extraction_status === 'in_progress' && (
                <div className="mt-3">
                  <div className="flex items-center text-sm text-yellow-700">
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Extracting requirements...
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Show main content only when requirements are ready */}
      {!requirementsNotReady && (
        <>
          {/* Header */}
          <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Vendor Responses</h2>
          <p className="text-sm text-gray-600">
            {pagination ? (
              <>
                {pagination.totalCount} total responses • Showing {filteredResponses.length} on page {pagination.currentPage} • Due {project.due_date ? formatDate(project.due_date) : 'No due date'}
              </>
            ) : (
              <>
                {filteredResponses.length} responses • Due {project.due_date ? formatDate(project.due_date) : 'No due date'}
              </>
            )}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm"
            onClick={onRefresh}
            disabled={!onRefresh}
            title="Refresh table"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {filteredResponses.length > 0 && (
            <>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleReEvaluateAllResponses}
                disabled={evaluatingIds.size > 0}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${evaluatingIds.size > 0 ? 'animate-spin' : ''}`} />
                Re-evaluate All
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleExportToExcel}
                disabled={isExporting}
                title="Export vendor responses to Excel"
              >
                <Download className={`w-4 h-4 mr-2 ${isExporting ? 'animate-pulse' : ''}`} />
                {isExporting ? 'Exporting...' : 'Export to Excel'}
              </Button>
            </>
          )}
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowBulkUploadModal(true)}
            disabled={!canUploadResponses}
            title={!canUploadResponses ? 'Requirements must be extracted first' : 'Upload vendor responses'}
          >
            <FolderOpen className="w-4 h-4 mr-2" />
            Upload Responses
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search vendors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="evaluated">Evaluated</option>
          <option value="ready_for_review">Ready for Review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Responses Table */}
      {filteredResponses.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Responses Yet</h3>
          <p className="text-gray-600 mb-4">
            {!canUploadResponses 
              ? 'You need to extract requirements before uploading vendor responses.'
              : 'No vendor responses have been submitted for this project. If you added the project using the NGEM extension, please wait a few more moments and then click on "Refresh".'
            }
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vendor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Files
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sections Submitted
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Meets Requirements
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredResponses.map((response) => (
                  <tr 
                    key={response.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/projects/${project.id}/vendors/${response.id}`)}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {response.vendor_name}
                        </div>
                        {response.vendor_email && (
                          <div className="text-sm text-gray-500">
                            {response.vendor_email}
                          </div>
                        )}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {response.file_count} file{response.file_count !== 1 ? 's' : ''}
                        </span>
                        {response.files?.some(f => f.status === 'uploading') && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <div className="w-1 h-1 bg-yellow-600 rounded-full animate-pulse mr-1"></div>
                            Processing
                          </span>
                        )}
                        {response.page_count && (
                          <span className="text-xs text-gray-500">
                            ({response.page_count} pages)
                          </span>
                        )}
                      </div>
                      {response.total_file_size && (
                        <div className="text-xs text-gray-500">
                          {Math.round(response.total_file_size / 1024 / 1024 * 100) / 100} MB
                        </div>
                      )}
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <div>
                          <div className="text-sm text-gray-900">
                            {formatDate(response.submission_date)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatRelativeTime(response.submission_date)}
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4">
                      <div className="relative flex items-center gap-2">
                        <select
                          value={response.status}
                          onChange={(e) => handleStatusChange(response.id, e.target.value)}
                          disabled={updatingStatusId === response.id || response.status === 'pending'}
                          className={`text-xs px-2 py-1 rounded border-0 font-medium cursor-pointer ${getStatusColor(response.status)} ${
                            updatingStatusId === response.id || response.status === 'pending' ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {response.status === 'pending' && (
                            <option value="pending">Pending</option>
                          )}
                          <option value="evaluated">Evaluated</option>
                          <option value="ready_for_review">Ready for Review</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Rejected</option>
                        </select>
                        {response.status === 'pending' && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" title="Analyzing..."></div>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4 text-center">
                      {(response.submitted_count !== undefined && response.submitted_count + (response.not_submitted_count || 0) > 0) ? (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          (response.mandatory_not_submitted_count || 0) > 0
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {response.submitted_count}/{response.submitted_count + (response.not_submitted_count || 0)}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>

                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Badge
                          variant="outline"
                          className={`text-xs ${getRequirementStatusColor(response.overall_requirement_status || 'pending')}`}
                        >
                          {getRequirementStatusLabel(response.overall_requirement_status || 'pending')}
                        </Badge>
                        {response.overall_requirement_status === 'pending' && (
                          <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse" title="Evaluating requirements..."></div>
                        )}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {response.unified_document_path && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDownloadUnifiedDocument(response)
                            }}
                            className="p-1 text-green-600 hover:text-green-800"
                            title="Download unified document"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleReEvaluateResponse(response.id)
                          }}
                          disabled={evaluatingIds.has(response.id)}
                          className="p-1 text-blue-600 hover:text-blue-800 disabled:opacity-50"
                          title="Re-evaluate"
                        >
                          <RefreshCw className={`w-4 h-4 ${evaluatingIds.has(response.id) ? 'animate-spin' : ''}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteResponse(response.id)
                          }}
                          disabled={deletingId === response.id}
                          className="p-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {pagination && pagination.totalCount > 0 && (
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => pagination.onPageChange(Math.max(1, pagination.currentPage - 1))}
                  disabled={pagination.currentPage <= 1}
                  className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => pagination.onPageChange(Math.min(pagination.totalPages, pagination.currentPage + 1))}
                  disabled={pagination.currentPage >= pagination.totalPages}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">{(pagination.currentPage - 1) * pagination.pageSize + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalCount)}
                    </span>{' '}
                    of <span className="font-medium">{pagination.totalCount}</span> results
                  </p>
                  <div className="flex items-center gap-2">
                    <label htmlFor="pageSize" className="text-sm text-gray-700">
                      Show:
                    </label>
                    <select
                      id="pageSize"
                      value={pagination.pageSize}
                      onChange={(e) => pagination.onPageSizeChange(Number(e.target.value))}
                      className="rounded-md border border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => pagination.onPageChange(Math.max(1, pagination.currentPage - 1))}
                      disabled={pagination.currentPage <= 1}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronDown className="h-5 w-5 rotate-90" aria-hidden="true" />
                    </button>

                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      const pageNum = Math.max(1, Math.min(pagination.totalPages - 4, pagination.currentPage - 2)) + i
                      if (pageNum > pagination.totalPages) return null

                      return (
                        <button
                          key={pageNum}
                          onClick={() => pagination.onPageChange(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                            pageNum === pagination.currentPage
                              ? 'z-10 bg-blue-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                              : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                          }`}
                        >
                          {pageNum}
                        </button>
                      )
                    })}

                    <button
                      onClick={() => pagination.onPageChange(Math.min(pagination.totalPages, pagination.currentPage + 1))}
                      disabled={pagination.currentPage >= pagination.totalPages}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronDown className="h-5 w-5 -rotate-90" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

          {/* Upload Modal */}
          <UploadVendorResponsesModal
            isOpen={showBulkUploadModal}
            onClose={() => setShowBulkUploadModal(false)}
            onSubmit={(vendors) => {
              if (onUploadVendorResponses) {
                return onUploadVendorResponses(vendors)
              }
              return Promise.resolve()
            }}
          />
        </>
      )}
    </div>
  )
}